/**
 * Country-code → primary IANA timezone, plus a tiny native-`Intl` "current
 * wall-clock time in a timezone" helper.
 *
 * WHY THIS EXISTS: the storefront's only need for `moment-timezone` in the
 * shared bundle was `checkSellingAvailability` (is the shop open right now,
 * in the shop's local time). `moment-timezone` ships the full IANA timezone
 * database — ~708KB parsed — into the site-wide client chunk just for that.
 *
 * This map is generated from `moment.tz.zonesForCountry(cc)[0]` for every
 * country moment knows, so `getPrimaryTimezoneForCountry` returns the *exact*
 * same zone the old code picked — behaviour-preserving — at ~2KB gzipped.
 * Regenerate (if ever needed) with:
 *   moment.tz.countries().reduce((m,c)=>{const z=moment.tz.zonesForCountry(c);
 *     if(z?.length)m[c]=z[0];return m;},{})
 */
export const COUNTRY_PRIMARY_TIMEZONE: Record<string, string> = {
  AD: "Europe/Andorra", AE: "Asia/Dubai", AF: "Asia/Kabul", AG: "America/Antigua",
  AI: "America/Anguilla", AL: "Europe/Tirane", AM: "Asia/Yerevan", AO: "Africa/Lagos",
  AQ: "Antarctica/Casey", AR: "America/Argentina/Buenos_Aires", AS: "Pacific/Pago_Pago", AT: "Europe/Vienna",
  AU: "Antarctica/Macquarie", AW: "America/Aruba", AX: "Europe/Helsinki", AZ: "Asia/Baku",
  BA: "Europe/Belgrade", BB: "America/Barbados", BD: "Asia/Dhaka", BE: "Europe/Brussels",
  BF: "Africa/Abidjan", BG: "Europe/Sofia", BH: "Asia/Bahrain", BI: "Africa/Bujumbura",
  BJ: "Africa/Lagos", BL: "America/Puerto_Rico", BM: "Atlantic/Bermuda", BN: "Asia/Brunei",
  BO: "America/La_Paz", BQ: "America/Kralendijk", BR: "America/Araguaina", BS: "America/Nassau",
  BT: "Asia/Thimphu", BW: "Africa/Gaborone", BY: "Europe/Minsk", BZ: "America/Belize",
  CA: "America/Atikokan", CC: "Asia/Yangon", CD: "Africa/Kinshasa", CF: "Africa/Bangui",
  CG: "Africa/Brazzaville", CH: "Europe/Zurich", CI: "Africa/Abidjan", CK: "Pacific/Rarotonga",
  CL: "America/Coyhaique", CM: "Africa/Douala", CN: "Asia/Shanghai", CO: "America/Bogota",
  CR: "America/Costa_Rica", CU: "America/Havana", CV: "Atlantic/Cape_Verde", CW: "America/Curacao",
  CX: "Asia/Bangkok", CY: "Asia/Famagusta", CZ: "Europe/Prague", DE: "Europe/Berlin",
  DJ: "Africa/Djibouti", DK: "Europe/Berlin", DM: "America/Dominica", DO: "America/Santo_Domingo",
  DZ: "Africa/Algiers", EC: "America/Guayaquil", EE: "Europe/Tallinn", EG: "Africa/Cairo",
  EH: "Africa/El_Aaiun", ER: "Africa/Asmara", ES: "Africa/Ceuta", ET: "Africa/Addis_Ababa",
  FI: "Europe/Helsinki", FJ: "Pacific/Fiji", FK: "Atlantic/Stanley", FM: "Pacific/Chuuk",
  FO: "Atlantic/Faroe", FR: "Europe/Paris", GA: "Africa/Lagos", GB: "Europe/London",
  GD: "America/Grenada", GE: "Asia/Tbilisi", GF: "America/Cayenne", GG: "Europe/Guernsey",
  GH: "Africa/Abidjan", GI: "Europe/Gibraltar", GL: "America/Danmarkshavn", GM: "Africa/Abidjan",
  GN: "Africa/Abidjan", GP: "America/Guadeloupe", GQ: "Africa/Lagos", GR: "Europe/Athens",
  GS: "Atlantic/South_Georgia", GT: "America/Guatemala", GU: "Pacific/Guam", GW: "Africa/Bissau",
  GY: "America/Guyana", HK: "Asia/Hong_Kong", HN: "America/Tegucigalpa", HR: "Europe/Belgrade",
  HT: "America/Port-au-Prince", HU: "Europe/Budapest", ID: "Asia/Jakarta", IE: "Europe/Dublin",
  IL: "Asia/Jerusalem", IM: "Europe/Isle_of_Man", IN: "Asia/Kolkata", IO: "Indian/Chagos",
  IQ: "Asia/Baghdad", IR: "Asia/Tehran", IS: "Africa/Abidjan", IT: "Europe/Rome",
  JE: "Europe/Jersey", JM: "America/Jamaica", JO: "Asia/Amman", JP: "Asia/Tokyo",
  KE: "Africa/Nairobi", KG: "Asia/Bishkek", KH: "Asia/Bangkok", KI: "Pacific/Kanton",
  KM: "Africa/Nairobi", KN: "America/Puerto_Rico", KP: "Asia/Pyongyang", KR: "Asia/Seoul",
  KW: "Asia/Kuwait", KY: "America/Cayman", KZ: "Asia/Almaty", LA: "Asia/Bangkok",
  LB: "Asia/Beirut", LC: "America/Puerto_Rico", LI: "Europe/Vaduz", LK: "Asia/Colombo",
  LR: "Africa/Monrovia", LS: "Africa/Johannesburg", LT: "Europe/Vilnius", LU: "Europe/Brussels",
  LV: "Europe/Riga", LY: "Africa/Tripoli", MA: "Africa/Casablanca", MC: "Europe/Monaco",
  MD: "Europe/Chisinau", ME: "Europe/Belgrade", MF: "America/Marigot", MG: "Africa/Nairobi",
  MH: "Pacific/Kwajalein", MK: "Europe/Belgrade", ML: "Africa/Abidjan", MM: "Asia/Yangon",
  MN: "Asia/Hovd", MO: "Asia/Macau", MP: "Pacific/Guam", MQ: "America/Martinique",
  MR: "Africa/Abidjan", MS: "America/Montserrat", MT: "Europe/Malta", MU: "Indian/Mauritius",
  MV: "Indian/Maldives", MW: "Africa/Blantyre", MX: "America/Bahia_Banderas", MY: "Asia/Kuala_Lumpur",
  MZ: "Africa/Maputo", NA: "Africa/Windhoek", NC: "Pacific/Noumea", NE: "Africa/Lagos",
  NF: "Pacific/Norfolk", NG: "Africa/Lagos", NI: "America/Managua", NL: "Europe/Amsterdam",
  NO: "Europe/Berlin", NP: "Asia/Kathmandu", NR: "Pacific/Nauru", NU: "Pacific/Niue",
  NZ: "Pacific/Auckland", OM: "Asia/Dubai", PA: "America/Panama", PE: "America/Lima",
  PF: "Pacific/Gambier", PG: "Pacific/Bougainville", PH: "Asia/Manila", PK: "Asia/Karachi",
  PL: "Europe/Warsaw", PM: "America/Miquelon", PN: "Pacific/Pitcairn", PR: "America/Puerto_Rico",
  PS: "Asia/Gaza", PT: "Atlantic/Azores", PW: "Pacific/Palau", PY: "America/Asuncion",
  QA: "Asia/Qatar", RE: "Asia/Dubai", RO: "Europe/Bucharest", RS: "Europe/Belgrade",
  RU: "Asia/Anadyr", RW: "Africa/Kigali", SA: "Asia/Riyadh", SB: "Pacific/Guadalcanal",
  SC: "Asia/Dubai", SD: "Africa/Khartoum", SE: "Europe/Berlin", SG: "Asia/Singapore",
  SH: "Africa/Abidjan", SI: "Europe/Belgrade", SJ: "Arctic/Longyearbyen", SK: "Europe/Bratislava",
  SL: "Africa/Abidjan", SM: "Europe/Rome", SN: "Africa/Abidjan", SO: "Africa/Mogadishu",
  SR: "America/Paramaribo", SS: "Africa/Juba", ST: "Africa/Sao_Tome", SV: "America/El_Salvador",
  SX: "America/Lower_Princes", SY: "Asia/Damascus", SZ: "Africa/Johannesburg", TC: "America/Grand_Turk",
  TD: "Africa/Ndjamena", TF: "Asia/Dubai", TG: "Africa/Abidjan", TH: "Asia/Bangkok",
  TJ: "Asia/Dushanbe", TK: "Pacific/Fakaofo", TL: "Asia/Dili", TM: "Asia/Ashgabat",
  TN: "Africa/Tunis", TO: "Pacific/Tongatapu", TR: "Europe/Istanbul", TT: "America/Port_of_Spain",
  TV: "Pacific/Funafuti", TW: "Asia/Taipei", TZ: "Africa/Dar_es_Salaam", UA: "Europe/Kyiv",
  UG: "Africa/Kampala", UM: "Pacific/Midway", US: "America/Adak", UY: "America/Montevideo",
  UZ: "Asia/Samarkand", VA: "Europe/Rome", VC: "America/Puerto_Rico", VE: "America/Caracas",
  VG: "America/Puerto_Rico", VI: "America/Puerto_Rico", VN: "Asia/Bangkok", VU: "Pacific/Efate",
  WF: "Pacific/Tarawa", WS: "Pacific/Apia", YE: "Asia/Aden", YT: "Africa/Nairobi",
  ZA: "Africa/Johannesburg", ZM: "Africa/Lusaka", ZW: "Africa/Harare",
};

/**
 * Primary IANA timezone for an ISO 3166-1 alpha-2 country code, or `null` if
 * unknown. Mirrors the old `moment.tz.zonesForCountry(cc)[0]` return contract
 * (callers treat `null` as "use the default zone").
 */
export const getPrimaryTimezoneForCountry = (
  countryCode: string,
): string | null => {
  if (!countryCode) return null;
  return COUNTRY_PRIMARY_TIMEZONE[countryCode.toUpperCase()] ?? null;
};

/**
 * Current wall-clock time in `timeZone`, expressed as minutes since midnight
 * (0–1439). Uses the platform `Intl` timezone database — supported on the
 * project's browser floor (iOS/Safari 15.4+) and in Node.
 */
export const nowMinutesInTimezone = (timeZone: string): number => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date());
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0") % 24;
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return hour * 60 + minute;
};

/** Parse an "HH:mm" wall-clock string to minutes since midnight. */
export const hhmmToMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(":");
  return (Number(h) || 0) * 60 + (Number(m) || 0);
};
